import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { LocalizedDatePipe } from 'src/app/angular-core/utils/pipes/localized-date/localized-date.pipe';
import { BasicXpacInfo, DeviceProblem } from 'src/app/generated/CrossPoint.RemoteXpac.Server';
import { PermissionsService } from 'src/app/services/permissions.service';
import { getDeviceProblemsAction } from 'src/app/store/actions/devices.actions';
import { selectDeviceProblems } from 'src/app/store/selectors/devices.selectors';
import { OperationEnum } from 'src/app/utils/enums/operation.enums';
import { hasValue } from 'src/app/angular-core/utils/functions/common.functions';
import { hasPermission } from 'src/app/utils/functions/permission.function';
import { getProblemInfoTranslation, getProblemTranslation } from 'src/app/utils/functions/problems.functions';
import { IProblemState, IRemoteXPACPortalState } from 'src/app/utils/interfaces/store.interfaces';

@UntilDestroy()
@Component({
  selector: 'xpac-ccap-general-info',
  templateUrl: './ccap-general-info.component.html',
  styleUrls: ['./ccap-general-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CcapGeneralInfoComponent implements OnInit {
  @Input() public ccap: BasicXpacInfo = null;

  public isLoading: boolean = true;
  public isLoadingProblems: boolean = true;
  public currentProblems: DeviceProblem[] = null;

  constructor(private store: Store<IRemoteXPACPortalState>,
    public dialog: MatDialog,
    private datePipe: LocalizedDatePipe,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private permissionsService: PermissionsService,
  ) { }

  public ngOnInit(): void {
    this.initListeners();
    this.initData();
  }

  private initListeners(): void {
    this.store.select(selectDeviceProblems(this.ccap.deviceId))
      .pipe(
        filter(hasValue),
        untilDestroyed(this)
      )
      .subscribe(({ problems }: IProblemState) => {
        this.currentProblems = problems;
        this.isLoadingProblems = false;
        this.checkLoading();
      });
  }

  private async initData(): Promise<void> {
    const permissions = await this.permissionsService.getPermissions();

    if (hasPermission('Device_Problems', permissions, OperationEnum.READ)) {
      this.store.dispatch(getDeviceProblemsAction({ data: this.ccap.deviceId }));      
    }
    else {
      this.isLoadingProblems = false;
    }

    this.checkLoading();
  }

  private checkLoading(): void {
    if (!this.isLoadingProblems && this.isLoading) {
      this.isLoading = false;
    }
    this.cdr.markForCheck();
  }

  public getOnlineStatus(): string {
    if (this.ccap.isConnected) {
      const fomattedTime = this.datePipe.transform(this.ccap.lastConnectionChangeUtc, 'short');
      return this.translate.instant('Modules.RemoteMaintenance.RemoteMaintenanceItemInfoData.OnlineSince', { timestamp: fomattedTime });
    }
    else if (this.ccap.lastConnectionChangeUtc) {
      const fomattedTime = this.datePipe.transform(this.ccap.lastConnectionChangeUtc, 'short');
      return this.translate.instant('Modules.RemoteMaintenance.RemoteMaintenanceItemInfoData.OfflineSince', { timestamp: fomattedTime });
    }
    else {
      return this.translate.instant('Modules.RemoteMaintenance.RemoteMaintenanceItemInfoData.Offline');
    }
  }

  public getProblemTitle(problem: DeviceProblem): string {
    return getProblemTranslation(problem, this.translate);
  }

  public getProblemInfo(problem: DeviceProblem): string {
    return getProblemInfoTranslation(problem);
  }
}
